import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FaPlay, FaPause, FaStop, FaExpand, FaCompress, FaTimes, 
  FaBookmark, FaCheck, FaClock, FaChartBar, FaDownload
} from 'react-icons/fa';
import apiClient from '../../apiClient';
import './ScormViewer.css';

const ScormViewer = ({ module, isOpen, onClose, onComplete }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scormData, setScormData] = useState(null);
  const [progress, setProgress] = useState({
    completion_status: 'incomplete',
    success_status: 'unknown',
    score: null,
    total_time: 0,
    lesson_location: '',
    suspend_data: ''
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  
  const iframeRef = useRef(null);
  const viewerRef = useRef(null);
  const intervalRef = useRef(null);

  // Backend URL for SCORM content
  const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://changemanagement-backend-ahfre3hvgcerbrgp.canadacentral-01.azurewebsites.net'
      : 'http://localhost:5000'
    );

  const saveProgress = useCallback(async () => {
    try {
      const progressData = {
        progress_percentage: progress.completion_status === 'completed' ? 100 : 
                           progress.completion_status === 'incomplete' ? 50 : 0,
        time_spent_minutes: Math.floor(timeSpent / 60),
        scorm_data: {
          completion_status: progress.completion_status,
          success_status: progress.success_status,
          score: progress.score,
          lesson_location: progress.lesson_location,
          suspend_data: progress.suspend_data,
          total_time: timeSpent
        }
      };

      await apiClient.post(`/training/modules/${module.id}/progress`, progressData);
      
      if (progress.completion_status === 'completed' && onComplete) {
        onComplete(module);
      }
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  }, [progress, timeSpent, module, onComplete]);

  const initializeScorm = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Load existing progress
      const progressResponse = await apiClient.get(`/training/modules/${module.id}/progress`);
      if (progressResponse.data) {
        setProgress(progressResponse.data);
        setTimeSpent(progressResponse.data.time_spent_minutes * 60 || 0);
      }

      // Parse SCORM data
      if (module.content_data) {
        try {
          const data = JSON.parse(module.content_data);
          setScormData(data);
        } catch (e) {
          console.error('Failed to parse SCORM data:', e);
        }
      }

      setIsPlaying(true);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to initialize SCORM content');
      setIsLoading(false);
      console.error('SCORM initialization error:', err);
    }
  }, [module]);

  const cleanupScorm = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Save final progress
    if (module && timeSpent > 0) {
      saveProgress();
    }
  }, [module, timeSpent, saveProgress]);

  useEffect(() => {
    if (isOpen && module) {
      initializeScorm();
    }
    return () => {
      cleanupScorm();
    };
  }, [isOpen, module, initializeScorm, cleanupScorm]);

  useEffect(() => {
    if (isPlaying && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    } else if (!isPlaying && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying]);

  const handlePlay = () => {
    setIsPlaying(true);
    if (iframeRef.current) {
      // Try to resume or start the SCORM content
      iframeRef.current.focus();
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
    saveProgress();
  };

  const handleStop = () => {
    setIsPlaying(false);
    setProgress(prev => ({ ...prev, completion_status: 'completed' }));
    saveProgress();
    onClose();
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (viewerRef.current.requestFullscreen) {
        viewerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getScormUrl = () => {
    if (!scormData) return '';
    
    const token = localStorage.getItem('access_token');
    const baseUrl = `${BACKEND_BASE_URL}/api/training/scorm/${scormData.scorm_id}/${scormData.launch_file}`;
    return token ? `${baseUrl}?token=${token}` : baseUrl;
  };

  const getCompletionColor = () => {
    switch (progress.completion_status) {
      case 'completed': return '#10b981';
      case 'incomplete': return '#f59e0b';
      case 'not attempted': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getSuccessColor = () => {
    switch (progress.success_status) {
      case 'passed': return '#10b981';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="scorm-viewer-overlay">
      <div 
        ref={viewerRef}
        className={`scorm-viewer ${isFullscreen ? 'fullscreen' : ''}`}
      >
        {/* Header */}
        <div className="scorm-viewer-header">
          <div className="header-left">
            <h2 className="module-title">{module.title}</h2>
            <div className="module-meta">
              <span className="difficulty">{module.difficulty_level}</span>
              {module.duration_minutes && (
                <span className="duration">
                  <FaClock /> {module.duration_minutes}m
                </span>
              )}
            </div>
          </div>
          
          <div className="header-controls">
            {!isLoading && (
              <>
                <button 
                  onClick={isPlaying ? handlePause : handlePlay}
                  className="control-btn primary"
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>
                
                <button 
                  onClick={handleStop}
                  className="control-btn"
                  title="Stop & Complete"
                >
                  <FaStop />
                </button>
                
                <button 
                  onClick={toggleFullscreen}
                  className="control-btn"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </>
            )}
            
            <button 
              onClick={onClose}
              className="control-btn close"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="scorm-progress-bar">
          <div className="progress-info">
            <span className="time-spent">
              <FaClock /> {formatTime(timeSpent)}
            </span>
            
            <div className="status-indicators">
              <span 
                className="completion-status"
                style={{ color: getCompletionColor() }}
              >
                <FaBookmark /> {progress.completion_status || 'not attempted'}
              </span>
              
              {progress.success_status !== 'unknown' && (
                <span 
                  className="success-status"
                  style={{ color: getSuccessColor() }}
                >
                  <FaCheck /> {progress.success_status}
                </span>
              )}
              
              {progress.score !== null && (
                <span className="score">
                  <FaChartBar /> {progress.score}%
                </span>
              )}
            </div>
          </div>
          
          <div className="progress-track">
            <div 
              className="progress-fill"
              style={{ 
                width: `${progress.completion_status === 'completed' ? 100 : 
                         progress.completion_status === 'incomplete' ? 50 : 0}%`,
                backgroundColor: getCompletionColor()
              }}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="scorm-content-area">
          {isLoading ? (
            <div className="scorm-loading">
              <div className="loading-spinner"></div>
              <p>Loading SCORM content...</p>
            </div>
          ) : error ? (
            <div className="scorm-error">
              <h3>Unable to Load Content</h3>
              <p>{error}</p>
              <button onClick={initializeScorm} className="retry-btn">
                Try Again
              </button>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={getScormUrl()}
              className="scorm-iframe"
              title={module.title}
              allow="fullscreen"
              onLoad={() => {
                setIsLoading(false);
                // Setup SCORM API bridge here if needed
                setupScormAPI();
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="scorm-viewer-footer">
          <div className="footer-left">
            <span className="module-type">SCORM Package</span>
            {module.tags && module.tags.length > 0 && (
              <div className="module-tags">
                {module.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag.tag_value}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="footer-right">
            <button 
              onClick={saveProgress}
              className="save-progress-btn"
              title="Save Progress"
            >
              <FaDownload /> Save Progress
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // SCORM API Bridge Setup
  function setupScormAPI() {
    // This would implement SCORM 1.2 or SCORM 2004 API
    // to communicate with the SCORM content for progress tracking
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const contentWindow = iframeRef.current.contentWindow;
      
      // SCORM 1.2 API
      contentWindow.API = {
        LMSInitialize: () => {
          console.log('SCORM: LMSInitialize called');
          return 'true';
        },
        
        LMSGetValue: (element) => {
          console.log('SCORM: LMSGetValue called for', element);
          
          switch (element) {
            case 'cmi.core.lesson_status':
              return progress.completion_status || 'not attempted';
            case 'cmi.core.score.raw':
              return progress.score || '';
            case 'cmi.core.lesson_location':
              return progress.lesson_location || '';
            case 'cmi.suspend_data':
              return progress.suspend_data || '';
            case 'cmi.core.total_time':
              return formatTime(timeSpent);
            default:
              return '';
          }
        },
        
        LMSSetValue: (element, value) => {
          console.log('SCORM: LMSSetValue called', element, value);
          
          switch (element) {
            case 'cmi.core.lesson_status':
              setProgress(prev => ({ ...prev, completion_status: value }));
              break;
            case 'cmi.core.score.raw':
              setProgress(prev => ({ ...prev, score: parseFloat(value) }));
              break;
            case 'cmi.core.lesson_location':
              setProgress(prev => ({ ...prev, lesson_location: value }));
              break;
            case 'cmi.suspend_data':
              setProgress(prev => ({ ...prev, suspend_data: value }));
              break;
            default:
              // Handle unknown SCORM elements
              console.warn('Unknown SCORM element:', element);
              break;
          }
          
          return 'true';
        },
        
        LMSCommit: () => {
          console.log('SCORM: LMSCommit called');
          saveProgress();
          return 'true';
        },
        
        LMSFinish: () => {
          console.log('SCORM: LMSFinish called');
          setProgress(prev => ({ ...prev, completion_status: 'completed' }));
          saveProgress();
          return 'true';
        },
        
        LMSGetLastError: () => '0',
        LMSGetErrorString: () => '',
        LMSGetDiagnostic: () => ''
      };
      
      // Also support SCORM 2004 API (similar but different namespace)
      contentWindow.API_1484_11 = contentWindow.API;
    }
  }
};

export default ScormViewer; 